import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Info } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  invoicesApi,
  Invoice,
  InvoiceStatus,
} from '../../api/invoices';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';

/**
 * Invoices across every business.
 *
 * **There is nothing to edit here, on purpose.** An invoice is a document
 * between a business and its customer, and PayIQ changing one after the fact
 * is forging a record their accounts depend on. A business that needs one
 * changed voids it and raises another; money that looks wrong is a
 * reconciliation question, which has its own page.
 *
 * What this is for is answering two questions without reading the database:
 * "the customer says they paid", and "why does my invoice say overdue".
 */

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part paid',
  paid: 'Paid',
  void: 'Void',
};

const STATUS_VARIANT: Record<
  InvoiceStatus,
  'neutral' | 'info' | 'warning' | 'success' | 'danger'
> = {
  draft: 'neutral',
  sent: 'info',
  part_paid: 'warning',
  paid: 'success',
  void: 'neutral',
};

const naira = (kobo: string | null | undefined) =>
  `₦${(Number(kobo ?? 0) / 100).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const date = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

const selectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

export const BusinessInvoices: React.FC = () => {
  usePage('Business Invoices');
  const toast = useToast();

  const [rows, setRows] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Invoice | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await invoicesApi.list({
        status: status || undefined,
        search: search.trim() || undefined,
        page,
        limit: 25,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      toast.error(err, 'Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  }, [status, search, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function open(invoice: Invoice) {
    // The list omits line items; the drawer needs them, so it re-reads.
    setSelected(invoice);
    try {
      setSelected(await invoicesApi.get(invoice.id));
    } catch (err) {
      toast.error(err, 'Could not load that invoice.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            background: 'var(--blue-100)',
            borderRadius: 'var(--radius-md)',
            padding: 14,
            marginBottom: 16,
          }}
        >
          <Info size={16} style={{ color: 'var(--blue-900)', flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: 13, color: 'var(--blue-900)', maxWidth: 680 }}>
            Read-only. An invoice is a document between a business and its
            customer — changing one here would forge a record their accounts
            depend on. A business that needs one changed voids it and raises
            another.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as InvoiceStatus | '');
              setPage(1);
            }}
            style={selectStyle}
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_LABEL) as InvoiceStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <div style={{ minWidth: 280 }}>
            <Input
              placeholder="Invoice number or customer"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title="No invoices match this filter." icon={FileText} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Invoice', 'Customer', 'Total', 'Outstanding', 'Due', 'Status', ''].map(
                      (h) => (
                        <th key={h} style={th}>
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => (
                    <tr key={inv.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={{ ...td, fontWeight: 600, color: 'var(--neutral-900)' }}>
                        {inv.number || 'Draft'}
                      </td>
                      <td style={td}>{inv.customerName}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(inv.totalKobo)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {naira(inv.balanceKobo)}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--neutral-500)' }}>
                        {date(inv.dueDate)}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Badge variant={STATUS_VARIANT[inv.status]}>
                            {STATUS_LABEL[inv.status]}
                          </Badge>
                          {/* Its own badge rather than a status of its own —
                              overdue is a sent invoice past its date, and
                              collapsing the two loses whether anything has
                              been paid. */}
                          {inv.overdue && <Badge variant="danger">Overdue</Badge>}
                        </div>
                      </td>
                      <td style={td}>
                        <Button variant="ghost" onClick={() => void open(inv)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil(total / 25))}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Drawer
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.number || 'Draft invoice'}
        subtitle={selected?.customerName}
        width={620}
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Section title="Lines">
              {(selected.items ?? []).length === 0 ? (
                <span style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
                  No lines recorded.
                </span>
              ) : (
                (selected.items ?? []).map((item) => (
                  <div
                    key={item.id}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13 }}
                  >
                    <span style={{ color: 'var(--neutral-700)' }}>
                      {item.description}
                      <span style={{ color: 'var(--neutral-500)' }}>
                        {' '}
                        · {Number(item.quantity)} × {naira(item.unitPriceKobo)}
                      </span>
                    </span>
                    <span style={{ color: 'var(--neutral-900)', whiteSpace: 'nowrap' }}>
                      {naira(item.lineTotalKobo)}
                    </span>
                  </div>
                ))
              )}
            </Section>

            <Section title="Totals">
              <Field label="Subtotal" value={naira(selected.subtotalKobo)} />
              <Field label="VAT (7.5%)" value={naira(selected.vatKobo)} />
              {/* Deducted by the customer and remitted to FIRS on the
                  business's behalf — it reduces what lands in the bank
                  without reducing what was earned. */}
              <Field
                label="Withholding tax (5%)"
                value={`−${naira(selected.withholdingKobo)}`}
              />
              <Field label="Total" value={naira(selected.totalKobo)} />
              <Field label="Paid" value={naira(selected.amountPaidKobo)} />
              <Field label="Outstanding" value={naira(selected.balanceKobo)} />
            </Section>

            <Section title="Dates and terms">
              <Field label="Issued" value={date(selected.issueDate)} />
              <Field label="Due" value={date(selected.dueDate)} />
              <Field label="Sent" value={date(selected.sentAt)} />
              <Field label="Paid" value={date(selected.paidAt)} />
              <Field label="Terms" value={selected.paymentTerms ?? '—'} />
              <Field label="PO reference" value={selected.purchaseOrderRef ?? '—'} />
              <Field label="Reminders sent" value={String(selected.remindersSent)} />
              {selected.voidReason && (
                <Field label="Voided because" value={selected.voidReason} />
              )}
            </Section>
          </div>
        )}
      </Drawer>
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h4
        style={{
          margin: 0,
          fontSize: 12,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'var(--neutral-500)',
        }}
      >
        {title}
      </h4>
      <div
        style={{
          border: '1px solid var(--neutral-300)',
          borderRadius: 'var(--radius-md)',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13 }}>
      <span style={{ color: 'var(--neutral-600)' }}>{label}</span>
      <span style={{ color: 'var(--neutral-900)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
